import { CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect } from 'react';

import { cn } from '@/shared/lib/cn';

import { useToastStore, type Toast, type ToastTone } from '@/shared/stores/toastStore';

const AUTO_DISMISS_MS = 4000;

const TONES: Record<ToastTone, { icon: typeof Info; classes: string }> = {
  success: { icon: CheckCircle2, classes: 'text-success' },
  error: { icon: XCircle, classes: 'text-danger' },
  info: { icon: Info, classes: 'text-info' },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { icon: Icon, classes } = TONES[toast.tone];

  // Auto-dismiss is a convenience, not a requirement: the close button is
  // always available.
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      role="status"
      className="bg-surface border-border shadow-(--shadow-overlay) flex items-center gap-2.5 rounded-(--radius-card) border px-3 py-2.5 text-sm"
    >
      <Icon className={cn('size-4 shrink-0', classes)} aria-hidden />
      <span className="text-ink min-w-0">{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Cerrar notificación"
        className="text-ink-subtle hover:bg-surface-sunken rounded-(--radius-control) -mr-1 p-1"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Renders the transient notifications. Mounted once in the shell; screens
 * publish via `useToast()` and never know where the message shows up.
 */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </div>
  );
}
